// =============================================================================
// FILE PATH: src/modules/pms/components/GanttChart.js
// =============================================================================

import React, { useEffect, useState, useRef } from "react";
import { getWBSTree, updateNodeDates } from "../services/wbsService";
import { getWBSDependencies } from "../services/wbsService";

const CELL_WIDTH = 30; // px per day

export default function GanttChart({ projectId }) {

  const [data, setData] = useState([]);
  const [dates, setDates] = useState([]);
  const [dragState, setDragState] = useState(null);
  const [dependencies, setDependencies] = useState([]);

  const containerRef = useRef();

    // 🔥 GROUP DEPENDENCIES BY SOURCE TASK (ADD HERE)
  const depGroupMap = React.useMemo(() => {
  const map = {};

  dependencies.forEach(dep => {
    if (!map[dep.predecessor_id]) {
      map[dep.predecessor_id] = [];
    }
    map[dep.predecessor_id].push(dep);
  });

  return map;
}, [dependencies]);
    
  useEffect(() => {
    loadData();
  }, [projectId]);

  const loadData = async () => {
    try {
      const res = await getWBSTree(projectId);
      const tree = res.data.data || [];

      const flat = flattenTree(tree);
      setData(flat);

      const { min, max } = getDateRange(flat);
      setDates(buildDates(min, max));

      try {
        const depRes = await getWBSDependencies(projectId);
        setDependencies(depRes.data.data || []);
      } catch (depErr) {
        console.error("Error loading dependencies", depErr);
        setDependencies([]);
      }

    } catch (err) {
      console.error("Error loading Gantt data", err);
    }
  };

  // ---------------- TREE → FLAT ----------------
  const flattenTree = (nodes, level = 0) => {
    let result = [];
    nodes.forEach(n => {
      result.push({ ...n, level });
      if (n.children) {
        result = result.concat(flattenTree(n.children, level + 1));
      }
    });
    return result;
  };

  // ---------------- DATE RANGE ----------------
  const getDateRange = (rows) => {
    let min = null, max = null;

    rows.forEach(r => {
      if (r.planned_start) {
        if (!min || r.planned_start < min) min = r.planned_start;
      }
      if (r.planned_end) {
        if (!max || r.planned_end > max) max = r.planned_end;
      }
    });

    return { min, max };
  };

  const buildDates = (min, max) => {
    if (!min || !max) return [];

    let arr = [];
    let current = new Date(min);
    let end = new Date(max);

    while (current <= end) {
      arr.push(new Date(current));
      current.setDate(current.getDate() + 1);
    }

    return arr;
  };

  // ---------------- POSITION CALC ----------------
  const getPosition = (row) => {
    if (!row.planned_start || !row.planned_end || dates.length === 0) return null;

    const start = new Date(row.planned_start);
    const end = new Date(row.planned_end);
    const first = dates[0];

    const offsetDays = Math.floor((start - first) / (1000 * 60 * 60 * 24));
    const durationDays = Math.floor((end - start) / (1000 * 60 * 60 * 24)) + 1;

    return {
      left: offsetDays * CELL_WIDTH,
      width: Math.max(durationDays * CELL_WIDTH, CELL_WIDTH)
    };
  };

  // ---------------- CRITICAL PATH ----------------
  const getCriticalTasks = () => {
    const map = {};
    dependencies.forEach(dep => {
      if (!map[dep.successor_id]) map[dep.successor_id] = [];
      map[dep.successor_id].push(dep.predecessor_id);
    });

    const critical = new Set();

    const visit = (taskId) => {
      critical.add(taskId);
      const preds = map[taskId] || [];
      preds.forEach(p => visit(p));
    };

    if (data.length > 0) {
      visit(data[data.length - 1].id);
    }

    return critical;
  };

  const criticalTasks = getCriticalTasks();

  // ---------------- DRAG START ----------------
  const onDragStart = (e, row, mode = "move") => {
    e.stopPropagation();

    setDragState({
      id: row.id,
      startX: e.clientX,
      originalStart: new Date(row.planned_start),
      originalEnd: new Date(row.planned_end),
      mode
    });
  };

  // ---------------- DRAG MOVE ----------------
  const onMouseMove = (e) => {
    if (!dragState) return;

    const dx = e.clientX - dragState.startX;
    const daysShift = Math.round(dx / CELL_WIDTH);

    setData(prev =>
      prev.map(r => {
        if (r.id !== dragState.id) return r;

        let newStart = new Date(dragState.originalStart);
        let newEnd = new Date(dragState.originalEnd);

        if (dragState.mode === "move") {
          newStart.setDate(newStart.getDate() + daysShift);
          newEnd.setDate(newEnd.getDate() + daysShift);
        }

        if (dragState.mode === "resize-left") {
          newStart.setDate(newStart.getDate() + daysShift);
          if (newStart > newEnd) newStart = new Date(newEnd);
        }

        if (dragState.mode === "resize-right") {
          newEnd.setDate(newEnd.getDate() + daysShift);
          if (newEnd < newStart) newEnd = new Date(newStart);
        }

        return {
          ...r,
          planned_start: newStart.toISOString().slice(0, 10),
          planned_end: newEnd.toISOString().slice(0, 10)
        };
      })
    );
  };

  // ---------------- DRAG END ----------------
  const onMouseUp = async () => {
    if (!dragState) return;

    const row = data.find(r => r.id === dragState.id);

    try {
      await updateNodeDates({
        node_id: row.id,
        planned_start: row.planned_start,
        planned_end: row.planned_end
      });
    } catch (err) {
      alert(err?.response?.data?.message || "Validation failed");
      loadData();
    }

    setDragState(null);
  };

  useEffect(() => {
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);

    return () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
    };
  });

  return (
    <div style={styles.container} ref={containerRef}>

      {/* HEADER */}
      <div style={styles.header}>
        <div style={styles.leftHeader}>Task</div>

        <div style={styles.timelineHeader}>
          {dates.map((d, i) => (
            <div key={i} style={styles.dateCell}>
              {d.getDate()}
            </div>
          ))}
        </div>
      </div>

      {/* BODY */}
      {data.map(row => {
        const pos = getPosition(row);

        return (
          <div key={row.id} style={styles.row}>

            <div style={{
              ...styles.leftCell,
              paddingLeft: row.level * 20
            }}>
              {row.name}
            </div>

            <div style={styles.timelineRow}>
              {pos && (
                <div
                  style={{
                    ...styles.bar,
                    background: criticalTasks.has(row.id) ? "#dc2626" : "#4f46e5",
                    left: pos.left,
                    width: pos.width
                  }}
                >
                  <div style={styles.handleLeft} onMouseDown={(e) => onDragStart(e, row, "resize-left")} />
                  <div style={styles.barBody} onMouseDown={(e) => onDragStart(e, row, "move")} />
                  <div style={styles.handleRight} onMouseDown={(e) => onDragStart(e, row, "resize-right")} />
                </div>
              )}
            </div>

          </div>
        );
      })}

      {/* 🔥 CURVED ARROWS */}
      <svg style={styles.svgOverlay}>
        {dependencies.map(dep => {

          const from = data.find(d => d.id === dep.predecessor_id);
          const to = data.find(d => d.id === dep.successor_id);

          if (!from || !to) return null;

          const fromPos = getPosition(from);
          const toPos = getPosition(to);

          if (!fromPos || !toPos) return null;

          const scrollLeft = containerRef.current?.scrollLeft || 0;

          const rowHeight = 28;
          const barOffset = 14;
          const headerOffset = 36;

          const fromIndex = data.findIndex(d => d.id === from.id);
          const toIndex = data.findIndex(d => d.id === to.id);

          const x1 = fromPos.left + fromPos.width - scrollLeft + 250;
          const x2 = toPos.left - scrollLeft + 250;

          //const depIndex = dependencies.findIndex(d => d.id === dep.id);

          // 🔥 LANE SEPARATION
          //const midX = x1 + 20 + (depIndex * 12);

          // 🔥 SMALL VERTICAL OFFSET
          //const yOffset = (depIndex % 2) * 4;

          // 🔥 SMART LANE BASED ON ROW DISTANCE

          //const rowGap = Math.abs(toIndex - fromIndex);
          //const laneSpacing = 18;
          //const lane = rowGap * laneSpacing;

          // forward lane
          //const midX = x1 + 30 + lane;

          // optional small Y offset (prevents overlap)
          //const yOffset = (rowGap % 2) * 4;
          // 🔥 STABLE LANE PER SOURCE TASK
          //const group = depGroupMap[from.id] || [];
          //const depIndex = group.findIndex(d => d.id === dep.id);

          // spacing per arrow from same source
          //const laneSpacing = 16;

          //const midX = x1 + 30 + (depIndex * laneSpacing);

          // small vertical offset
          //const yOffset = depIndex * 3;
          
          const group = depGroupMap[from.id] || [];
          const depIndex = group.findIndex(d => d.id === dep.id);

          // spacing logic
          const baseOffset = 30;
          const laneSpacing = 18;

          // spread arrows based on row distance
          const rowGap = Math.abs(toIndex - fromIndex);
          const lane = depIndex * laneSpacing + rowGap * 4;

          const midX = x1 + baseOffset + lane;

          // vertical offset
          const yOffset = depIndex * 4;
          
          const y1 = fromIndex * rowHeight + headerOffset + barOffset + yOffset;
          const y2 = toIndex * rowHeight + headerOffset + barOffset + yOffset;

          const gap = 20;

          let pathData = "";

          if (x2 >= x1) {
            pathData = `
              M ${x1} ${y1}
              L ${midX} ${y1}
              L ${midX} ${y2}
              L ${x2} ${y2}
            `;
          } else {
              const backX = x1 + 50 + (depIndex * 18);

              pathData = `
                M ${x1} ${y1}
                L ${backX} ${y1}
                L ${backX} ${y1 + 30}
                L ${x2 - 30} ${y1 + 30}
                L ${x2 - 30} ${y2}
                L ${x2} ${y2}
              `;
          
          }

          return (
            <path
              key={dep.id}
              d={pathData}
              fill="none"
              stroke="#ef4444"
              strokeWidth="2"
              markerEnd="url(#arrow)"
            />
          );
        })}

        <defs>
          <marker id="arrow" markerWidth="10" markerHeight="10" refX="10" refY="3" orient="auto">
            <path d="M0,0 L10,3 L0,6" fill="#ef4444" />
          </marker>
        </defs>
      </svg>

    </div>
  );
}

// ==========================================================
// STYLES
// ==========================================================
const styles = {
  container: {
    border: "1px solid #ddd",
    overflow: "auto",
    position: "relative"
  },

  header: {
    display: "flex",
    borderBottom: "1px solid #ddd",
    background: "#f9fafb"
  },

  leftHeader: {
    width: 250,
    padding: 10,
    fontWeight: "bold"
  },

  timelineHeader: {
    display: "flex"
  },

  dateCell: {
    width: 30,
    textAlign: "center",
    fontSize: 12,
    padding: 6
  },

  row: {
    display: "flex",
    borderBottom: "1px solid #eee"
  },

  leftCell: {
    width: 250,
    padding: 8
  },

  timelineRow: {
    position: "relative",
    height: 28,
    flex: 1
  },

  bar: {
    position: "absolute",
    height: 20,
    borderRadius: 4,
    display: "flex",
    alignItems: "center"
  },

  barBody: {
    flex: 1,
    height: "100%",
    cursor: "grab"
  },

  handleLeft: {
    width: 6,
    height: "100%",
    background: "#312e81",
    cursor: "ew-resize"
  },

  handleRight: {
    width: 6,
    height: "100%",
    background: "#312e81",
    cursor: "ew-resize"
  },

  svgOverlay: {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    pointerEvents: "none",
    zIndex: 5
  }
};